import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useSnackbar } from 'notistack'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getMedicalConditionTypeList } from 'app/redux/store/features/medcond'
import MedicalConditionTypeGridData from './components/MedicalConditionTypeGridData'
import MedicalConditionTypeGridFilter from './components/MedicalConditionTypeGridFilter'
const sortFieldMap = {
    name: 'Name',
    displayOrder: 'DisplayOrder',
    createTime: 'CreateTime',
    updateTime: 'UpdateTime',
}

const MedicalConditionTypeGrid = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const [filter, setFilter] = useState({
        name: '',
        sort: 'DisplayOrder',
        isDescending: false,
    })
    const { list: medicalConditionTypes, loading, error } = useSelector((state) => state.medcond)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25
    const dispatch = useDispatch()

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        setApiCallInProgress(true)
    }, [dispatch])

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !medicalConditionTypes.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getMedicalConditionTypeList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function ageGroupReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [medicalConditionTypeGrid, setMedicalConditionTypeGrid] = React.useReducer(
        ageGroupReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getMedicalConditionTypeList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getMedicalConditionTypeList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const key = sortOrder?.[0]?.columnName || 'displayOrder'
        const sortProperty = sortFieldMap[key] ?? key
        const currentFilter = {
            ...filter,
            sort: sortProperty,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getMedicalConditionTypeList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getMedicalConditionTypeList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setMedicalConditionTypeGrid({ type: 'set-grid-filter', payload: value })
        },
        [setMedicalConditionTypeGrid]
    )

    const onAddAgeGroup = () => {
        navigate('/settings/definitions/medical-condition-type/add')
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setMedicalConditionTypeGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setMedicalConditionTypeGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.5,
                            ml: -2,
                            mr: 0.5,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Medical condition types
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'success.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddAgeGroup}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                medicalConditionTypeGrid.filterIconColor && !medicalConditionTypeGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!medicalConditionTypeGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <MedicalConditionTypeGridFilter
                    mutation={applyFilter}
                    display={medicalConditionTypeGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <MedicalConditionTypeGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default MedicalConditionTypeGrid
